import React, { useEffect, useState } from "react";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Checkbox from "components/form/checkbox/Checkbox";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import { Select as SelectNew, Select } from "components/select/";
import { useCustomerContext } from "../../context/CustomerContext";
import { PostAccountScheduledPayment } from "api/rpc/2022-09/facilityAdmin/client/accounts";
import { capitalize, formatDate } from "helpers/Helpers";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { showError, showSuccess } from "redux/actions/ui";
import Callout from "components/callout/Callout";
import { CancelToken } from "axios";
import { useTranslation } from "react-i18next";
import { Dropdown } from "components/dropdown/dropdown";

type AccountScheduledPaymentProps = {
  open: boolean;
  onClose: () => void;
  onOk: () => Promise<void>;
};

interface IScheduledPayment {
  amount: string;
  date: Date;
  paymentMethodId: number;
  chargeStatementBalance: boolean;
  recurringPayment: boolean;
  frequencyType: string;
  frequencyValue: number;
  note: string;
}

export default function AccountScheduledPaymentModal(props: AccountScheduledPaymentProps) {
  const { open, onClose, onOk } = props;
  const { state, updateState } = useCustomerContext();
  const dispatch = useAppDispatch();
  const { Option } = SelectNew;
  const { t } = useTranslation();

  const [scheduledPayment, setScheduledPayment] = useState<IScheduledPayment>({
    amount: "0",
    date: new Date(),
    paymentMethodId: null,
    chargeStatementBalance: false,
    recurringPayment: false,
    frequencyType: "monthly",
    frequencyValue: 1,
    note: "",
  });

  useEffect(() => {
    if (open && state?.paymentMethods && state?.paymentMethods?.length > 0) {
      setScheduledPayment(prevState => ({ ...prevState, paymentMethodId: state?.paymentMethods[0]?.id }));
    }
  }, [state?.paymentMethods, open]);

  function handleDateChange(date: Date) {
    setScheduledPayment(prevState => ({ ...prevState, date }));
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setScheduledPayment(prevState => ({ ...prevState, [id]: value }));
  }

  function handlePaymentMethodChange(id: number) {
    setScheduledPayment(prevState => ({ ...prevState, paymentMethodId: id }));
  }

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    setScheduledPayment(prevState => ({ ...prevState, [id]: checked, amount: checked ? "0" : prevState?.amount }));
  }

  function handleReoccuringCheckbox(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    setScheduledPayment(prevState => ({ ...prevState, [id]: checked, frequencyValue: 0 }));
  }

  function handleFrequencyChange(frequency: string) {
    setScheduledPayment(prevState => ({ ...prevState, frequencyType: frequency }));
  }

  function handleOnClose() {
    setScheduledPayment(prevState => ({
      ...prevState,
      amount: "0",
      date: new Date(),
      paymentMethodId: null,
      chargeStatementBalance: false,
      note: "",
      frequencyType: "monthly",
      frequencyValue: 0,
      recurringPayment: false,
    }));
    void onClose();
  }

  async function createScheduledPayment() {
    // Check if date is in the future
    const now = new Date();
    if (scheduledPayment?.date <= now) {
      dispatch(showError(t("Payment date must be in the future")));
      return;
    }

    const res = await PostAccountScheduledPayment(
      {
        account_id: state?.selectedHouseAccount?.id,
        payment_method_id: scheduledPayment?.paymentMethodId,
        amount: Number(scheduledPayment?.amount),
        process_date: formatDate(scheduledPayment?.date),
        charge_balance: scheduledPayment?.chargeStatementBalance,
        reoccur: scheduledPayment?.frequencyValue,
        frequency: scheduledPayment?.frequencyType,
        note: scheduledPayment?.note,
      },
      true,
    );
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating scheduled payment")));
      return;
    }
    dispatch(showSuccess(t("Successfully created scheduled payment")));
    void handleOnClose();
    await onOk();
  }

  const disableOK =
    scheduledPayment.amount === "" ||
    isNaN(Number(scheduledPayment.amount)) ||
    !scheduledPayment.date ||
    !scheduledPayment.paymentMethodId;

  return (
    <Sheet
      open={open}
      size="small"
      height="flexible"
      closable
      title={t("Scheduled Payment")}
      onCancel={handleOnClose}
      onOk={createScheduledPayment}
      cancelText={t("Cancel")}
      okText={t("Add")}
      overflow
      okDisabled={disableOK}
    >
      {state?.paymentMethods?.length === 0 ? (
        <Callout
          type="error"
          title={t("No Saved Payment Methods")}
          content={t("This customer does not have any payment methods saved. Add a new payment method to create a scheduled payment.")}
        />
      ) : (
        <Form>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("Amount")}
                type="number"
                id="amount"
                value={scheduledPayment.amount}
                onChange={handleInputChange}
                disabled={scheduledPayment?.chargeStatementBalance}
              />
              <DatePickerInput
                months={1}
                position="left"
                startingDate={scheduledPayment.date}
                setStartingDate={handleDateChange}
                label={t("Payment Date")}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label={t("Payment Method")}
                onChange={handlePaymentMethodChange}
                defaultValue={scheduledPayment?.paymentMethodId}
              >
                {state?.paymentMethods?.length > 0 &&
                  state?.paymentMethods?.map((card, index) => {
                    return (
                      <Option key={index} value={card?.id}>
                        {capitalize(card?.brand)} •••• •••• •••• {card?.last4}
                      </Option>
                    );
                  })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label={t("Note")}
                id="note"
                value={scheduledPayment.note}
                onChange={handleInputChange}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Checkbox
                size="medium"
                value={scheduledPayment.chargeStatementBalance}
                checked={scheduledPayment.chargeStatementBalance}
                onChange={handleCheckboxChange}
                label={t("Charge full statement balance")}
                id="chargeStatementBalance"
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Checkbox
                size="medium"
                value={scheduledPayment.recurringPayment}
                checked={scheduledPayment.recurringPayment}
                onChange={handleReoccuringCheckbox}
                label={t("Recurring Payment")}
                id="recurringPayment"
              />
            </FormLayout.Group>

            {scheduledPayment.recurringPayment ? (
              <FormLayout.Group>
                <Input
                  label={t("Duration")}
                  id="frequencyValue"
                  type="number"
                  value={scheduledPayment.frequencyValue}
                  onChange={handleInputChange}
                />

                <Select
                  label={t("Frequency")}
                  onChange={handleFrequencyChange}
                  defaultValue={scheduledPayment?.frequencyType}
                >
                  <Option value="monthly">{t('Monthly')}</Option>
                  <Option value="bi-weekly">{t('Bi-Weekly')}</Option>
                  <Option value="weekly">{t('Weekly')}</Option>
                </Select>
              </FormLayout.Group>
            ) : null}
          </FormLayout>
        </Form>
      )}
    </Sheet>
  );
}
